import type { OrgRosterRow, Unit } from '@unit/shared';

/**
 * The org grid's data shaping, kept out of the component so it is testable
 * without rendering AG Grid (jsdom does not lay it out).
 */

/** Per-unit counts for the structure panel: a platoon's row sums its teams. */
export interface UnitCounts {
  soldiers: number;
  openGear: number;
  unsignedForms: number;
  overdue: number;
}

export function countsByUnit(rows: OrgRosterRow[], units: Unit[]): Map<string, UnitCounts> {
  const out = new Map<string, UnitCounts>();
  const bump = (id: string | null, row: OrgRosterRow) => {
    if (!id) return;
    const c = out.get(id) ?? { soldiers: 0, openGear: 0, unsignedForms: 0, overdue: 0 };
    if (row.status === 'active') c.soldiers += 1;
    c.openGear += row.openGear;
    c.unsignedForms += row.unsignedForms;
    c.overdue += row.overdue;
    out.set(id, c);
  };
  for (const row of rows) {
    bump(row.teamId, row);
    bump(row.platoonId, row);
  }
  for (const u of units) if (!out.has(u.id)) out.set(u.id, { soldiers: 0, openGear: 0, unsignedForms: 0, overdue: 0 });
  return out;
}

/** The rows the grid shows for one selected unit (a platoon includes its teams), or all of them. */
export function rowsForUnit(rows: OrgRosterRow[], unitId: string | null): OrgRosterRow[] {
  if (!unitId) return rows;
  return rows.filter((r) => r.teamId === unitId || r.platoonId === unitId);
}

/** Company-wide totals for the tiles. */
export function totals(rows: OrgRosterRow[]): UnitCounts {
  const t = { soldiers: 0, openGear: 0, unsignedForms: 0, overdue: 0 };
  for (const r of rows) {
    if (r.status === 'active') t.soldiers += 1;
    t.openGear += r.openGear;
    t.unsignedForms += r.unsignedForms;
    t.overdue += r.overdue;
  }
  return t;
}
